import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    getRestaurantMenuAnalyticsErrorMessage,
    resetRestaurantMenuAnalyticsFilters,
    selectRestaurantMenuAnalyticsFilters,
    setRestaurantMenuAnalyticsMenuIds,
    setRestaurantMenuAnalyticsPage,
    setRestaurantMenuAnalyticsPeriod,
    setRestaurantMenuAnalyticsStatuses,
    setRestaurantMenuAnalyticsTableId,
} from "@/features/restaurant-menu-analytics"
import {
    getOrder,
    IListRestaurantMenuOrdersResponse,
    IRestaurantMenuOrderCard,
    IRestaurantMenuOrderListItem,
    listOrders,
} from "@/shared/api/restaurant-menu-orders"
import { IRestaurantMenu, listRestaurantMenus } from "@/shared/api/restaurant-menus"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Loader } from "@/shared/ui/loader"
import { Pagination } from "@/shared/ui/pagination"
import { PeriodPicker } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { PageHeader } from "@/widgets/page-header"

const PAGE_SIZE = 25
const ALL_OPTION_VALUE = "__all__"
// Canonical menu-order statuses (order.go domain @ N4): draft/processing/accepted/completed/cancelled/failed.
const COMMON_STATUSES = ["draft", "processing", "accepted", "completed", "cancelled", "failed"]

type LoadStatus = "idle" | "loading" | "loaded" | "error"

interface OrderDetailsState {
    status: LoadStatus
    data: IRestaurantMenuOrderCard | null
    error: string | null
}

const formatMoney = (value?: number): string => {
    if (typeof value !== "number") return "—"

    return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "RUB",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value)
}

const formatNumber = (value?: number | string): string => {
    if (typeof value === "undefined") return "—"
    const normalized = Number(value)
    return Number.isFinite(normalized) ? new Intl.NumberFormat("ru-RU").format(normalized) : String(value)
}

const formatDateTime = (value: string | null): string => {
    if (!value) return "—"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "—"

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date)
}

const getProcessedPaymentsText = (order: IRestaurantMenuOrderCard): string => {
    if (order.iiko.availability === "NO_DATA") return "нет данных"
    if (order.iiko.availability === "UNAVAILABLE") return "недоступно"
    return typeof order.iiko.processedPaymentsSum === "number"
        ? formatMoney(order.iiko.processedPaymentsSum)
        : "—"
}

const EmptyValue = () => <span className="text-[#828282]">—</span>

interface OrderDetailsProps {
    state?: OrderDetailsState
    onRetry: () => void
}

const OrderDetails = ({ state, onRetry }: OrderDetailsProps) => {
    if (!state || state.status === "loading" || state.status === "idle") {
        return (
            <div className="min-h-[120px] flex items-center justify-center">
                <Loader />
            </div>
        )
    }

    if (state.status === "error") {
        return (
            <div className="flex flex-wrap items-center justify-between gap-4 py-4">
                <span className="text-color-danger">{state.error}</span>
                <button type="button" onClick={onRetry} className="form_handle_button !w-auto px-6">
                    Повторить
                </button>
            </div>
        )
    }

    const order = state.data
    if (!order) return null

    return (
        <div className="grid grid-cols-1 gap-6 py-3 lg:grid-cols-2">
            <div>
                <h3 className="mb-3 text-base font-bold">Позиции</h3>
                {order.items.length === 0 && <div className="text-[#828282]">Нет данных о позициях</div>}
                <div className="flex flex-col gap-3">
                    {order.items.map((item, index) => (
                        <div key={item.id || index} className="rounded-xl bg-white p-3">
                            <div className="flex flex-wrap justify-between gap-2 whitespace-normal">
                                <span>{item.name || "Без названия"} × {item.quantity ?? "—"}</span>
                                <span>
                                    {formatMoney(item.price)}
                                    {typeof item.calculatedTotal === "number" && ` · итого ${formatMoney(item.calculatedTotal)}`}
                                </span>
                            </div>
                            {item.modifiers.length > 0 && (
                                <div className="mt-2 flex flex-col gap-1 border-l-2 border-[#E7EAEE] pl-3 text-sm text-[#5F666C]">
                                    {item.modifiers.map((modifier, modifierIndex) => (
                                        <div key={modifier.id || modifierIndex} className="flex flex-wrap justify-between gap-2 whitespace-normal">
                                            <span>{modifier.name || "Модификатор"} × {modifier.quantity ?? "—"}</span>
                                            <span>{formatMoney(modifier.price)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-4 whitespace-normal">
                    <span className="font-bold">Комментарий: </span>
                    {order.comment || <EmptyValue />}
                </div>
            </div>
            <div className="grid content-start grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-white p-3">
                    <div className="text-sm text-[#828282]">Статус заказа</div>
                    <div className="mt-1 whitespace-normal">{order.status || <EmptyValue />}</div>
                </div>
                <div className="rounded-xl bg-white p-3">
                    <div className="text-sm text-[#828282]">Статус IIKO</div>
                    <div className="mt-1 whitespace-normal">{order.iiko.orderStatus || <EmptyValue />}</div>
                </div>
                <div className="rounded-xl bg-white p-3">
                    <div className="text-sm text-[#828282]">Статус оплаты</div>
                    <div className="mt-1 whitespace-normal">{order.iiko.paymentStatus || <EmptyValue />}</div>
                </div>
                <div className="rounded-xl bg-white p-3">
                    <div className="text-sm text-[#828282]">Проведено</div>
                    <div className="mt-1 whitespace-normal">{getProcessedPaymentsText(order)}</div>
                </div>
                <div className="rounded-xl bg-white p-3">
                    <div className="text-sm text-[#828282]">ID заказа</div>
                    <div className="mt-1 break-all whitespace-normal">{order.id || <EmptyValue />}</div>
                </div>
                <div className="rounded-xl bg-white p-3">
                    <div className="text-sm text-[#828282]">ID заказа IIKO</div>
                    <div className="mt-1 break-all whitespace-normal">{order.iikoOrderId || <EmptyValue />}</div>
                </div>
            </div>
        </div>
    )
}

export const RestaurantMenuOrdersPage = () => {
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectRestaurantMenuAnalyticsFilters)
    const ordersRequestId = useRef(0)
    const [ordersStatus, setOrdersStatus] = useState<LoadStatus>("idle")
    const [ordersData, setOrdersData] = useState<IListRestaurantMenuOrdersResponse | null>(null)
    const [ordersError, setOrdersError] = useState<string | null>(null)
    const [menus, setMenus] = useState<IRestaurantMenu[]>([])
    const [menusStatus, setMenusStatus] = useState<LoadStatus>("idle")
    const [menusError, setMenusError] = useState<string | null>(null)
    const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null)
    const [detailsById, setDetailsById] = useState<Record<string, OrderDetailsState>>({})
    // Local draft so typing a table id doesn't refire the orders request on every keystroke —
    // it is applied to the store (which drives the fetch) only on Enter / blur.
    const [tableDraft, setTableDraft] = useState(filters.tableId)

    useEffect(() => {
        setTableDraft(filters.tableId)
    }, [filters.tableId])

    const applyTableId = () => {
        const next = tableDraft.trim()
        if (next !== filters.tableId) dispatch(setRestaurantMenuAnalyticsTableId(next))
    }

    const fetchMenus = useCallback(async () => {
        setMenusStatus("loading")
        setMenusError(null)
        try {
            const response = await listRestaurantMenus()
            setMenus(response.data.menus)
            setMenusStatus("loaded")
        } catch (error) {
            setMenus([])
            setMenusStatus("error")
            setMenusError(getRestaurantMenuAnalyticsErrorMessage(error))
        }
    }, [])

    useEffect(() => {
        fetchMenus()
    }, [fetchMenus])

    const fetchOrders = useCallback(async () => {
        const requestId = ++ordersRequestId.current
        setOrdersStatus("loading")
        setOrdersData(null)
        setOrdersError(null)
        setExpandedOrderId(null)

        try {
            const response = await listOrders({
                periodFrom: filters.period.from,
                periodTo: filters.period.to,
                menuIds: filters.menuIds,
                statuses: filters.statuses,
                tableIds: filters.tableId.trim() ? [filters.tableId.trim()] : [],
                page: filters.page,
                pageSize: PAGE_SIZE,
            })
            if (ordersRequestId.current !== requestId) return
            setOrdersData(response.data)
            setOrdersStatus("loaded")
        } catch (error) {
            if (ordersRequestId.current !== requestId) return
            setOrdersData(null)
            setOrdersStatus("error")
            setOrdersError(getRestaurantMenuAnalyticsErrorMessage(error))
        }
    }, [filters.menuIds, filters.page, filters.period.from, filters.period.to, filters.statuses, filters.tableId])

    useEffect(() => {
        fetchOrders()
    }, [fetchOrders])

    const fetchOrderDetails = useCallback(async (orderId: string) => {
        setDetailsById((current) => ({
            ...current,
            [orderId]: { status: "loading", data: null, error: null },
        }))

        try {
            const response = await getOrder(orderId)
            setDetailsById((current) => ({
                ...current,
                [orderId]: { status: "loaded", data: response.data, error: null },
            }))
        } catch (error) {
            setDetailsById((current) => ({
                ...current,
                [orderId]: {
                    status: "error",
                    data: null,
                    error: getRestaurantMenuAnalyticsErrorMessage(error),
                },
            }))
        }
    }, [])

    const handleOrderClick = (order: IRestaurantMenuOrderListItem) => {
        if (!order.id) return
        if (expandedOrderId === order.id) {
            setExpandedOrderId(null)
            return
        }

        setExpandedOrderId(order.id)
        if (!detailsById[order.id]) {
            fetchOrderDetails(order.id)
        }
    }

    const menuNameById = useMemo(() => new Map(
        menus.filter((menu) => menu.id).map((menu) => [menu.id as string, menu.name || menu.id as string])
    ), [menus])

    const menuOptions = useMemo(() => {
        const loadedOptions = menus.filter((menu) => menu.id).map((menu) => ({
            value: menu.id as string,
            label: menu.name || menu.id as string,
        }))
        const loadedIds = new Set(loadedOptions.map((option) => option.value))
        const persistedOptions = filters.menuIds
            .filter((menuId) => !loadedIds.has(menuId))
            .map((menuId) => ({ value: menuId, label: menuId }))

        return [
            { value: ALL_OPTION_VALUE, label: "Все меню" },
            ...loadedOptions,
            ...persistedOptions,
        ]
    }, [filters.menuIds, menus])

    const selectedMenuOptions = useMemo(
        () => menuOptions.filter((option) => option.value !== ALL_OPTION_VALUE && filters.menuIds.includes(option.value)),
        [filters.menuIds, menuOptions]
    )

    const statusOptions = useMemo(() => {
        const values = new Set([
            ...COMMON_STATUSES,
            ...filters.statuses,
            ...(ordersData?.orders.map((order) => order.status).filter(Boolean) as string[] || []),
        ])
        return [
            { value: ALL_OPTION_VALUE, label: "Все статусы" },
            ...Array.from(values).map((status) => ({ value: status, label: status })),
        ]
    }, [filters.statuses, ordersData])

    const selectedStatusOptions = useMemo(
        () => statusOptions.filter((option) => option.value !== ALL_OPTION_VALUE && filters.statuses.includes(option.value)),
        [filters.statuses, statusOptions]
    )

    const appliedFiltersCount = 1
        + Number(filters.menuIds.length > 0)
        + Number(filters.statuses.length > 0)
        + Number(Boolean(filters.tableId.trim()))

    const totalCount = Number(ordersData?.total ?? 0)
    const totalPages = Math.max(1, Math.ceil(totalCount / (ordersData?.pageSize || PAGE_SIZE)))

    return (
        <div className="_page_container">
            <PageHeader title="Заказы" />
            <div className="flexbox">
                <div className="box">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-lg font-bold screen:text-2xl">Фильтры</h2>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm text-[#5F666C]">Применено фильтров: {appliedFiltersCount}</span>
                            <button
                                type="button"
                                onClick={() => dispatch(resetRestaurantMenuAnalyticsFilters())}
                                className="form_cancel_button !h-10 !w-auto px-5"
                            >
                                Сбросить
                            </button>
                        </div>
                    </div>
                    <PeriodPicker
                        value={filters.period}
                        onChange={(period) => dispatch(setRestaurantMenuAnalyticsPeriod(period))}
                        withInterval={false}
                    />
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <Select
                            isMulti
                            isLoading={menusStatus === "loading"}
                            options={menuOptions}
                            value={selectedMenuOptions}
                            placeholder="Все меню"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                dispatch(setRestaurantMenuAnalyticsMenuIds(
                                    values.includes(ALL_OPTION_VALUE) ? [] : values
                                ))
                            }}
                        />
                        <input
                            value={tableDraft}
                            onChange={(event) => setTableDraft(event.target.value)}
                            onBlur={applyTableId}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") applyTableId()
                            }}
                            placeholder="Все столы / ID стола (Enter)"
                            className="h-[52px] w-full rounded-full bg-white px-5 text-color-dark outline-none ring-1 ring-transparent focus:ring-color-dark"
                        />
                        <Select
                            isMulti
                            options={statusOptions}
                            value={selectedStatusOptions}
                            placeholder="Все статусы"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                dispatch(setRestaurantMenuAnalyticsStatuses(
                                    values.includes(ALL_OPTION_VALUE) ? [] : values
                                ))
                            }}
                        />
                    </div>
                    {menusStatus === "error" && (
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#FFF4F4] p-3">
                            <span className="text-color-danger">Меню не загружены: {menusError}. В таблице будут показаны их ID.</span>
                            <button type="button" onClick={fetchMenus} className="form_handle_button !h-10 !w-auto px-5">
                                Повторить
                            </button>
                        </div>
                    )}
                </div>

                {ordersData?.periodTruncated && (
                    <div className="info_panel !bg-[#FFF8E7]">
                        Показаны данные максимум за {ordersData.limits.maxPeriodDays} дней, период был ограничен сервером.
                    </div>
                )}
                {ordersData?.pageSizeCapped && (
                    <div className="info_panel !bg-[#FFF8E7]">
                        На странице показано не более {ordersData.limits.maxPageSize} заказов: размер страницы был ограничен сервером.
                    </div>
                )}

                {ordersStatus === "loading" && (
                    <div className="info_panel">
                        <Loader />
                    </div>
                )}

                {ordersStatus === "error" && (
                    <div className="info_panel flex flex-wrap items-center justify-between gap-4">
                        <span className="text-color-danger">{ordersError}</span>
                        <button type="button" onClick={fetchOrders} className="form_handle_button !w-auto px-6">
                            Повторить
                        </button>
                    </div>
                )}

                {ordersStatus === "loaded" && ordersData?.orders.length === 0 && (
                    <div className="info_panel text-lg">Нет данных за период</div>
                )}

                {ordersStatus === "loaded" && Boolean(ordersData?.orders.length) && (
                    <div className="table_box">
                        <div className="overflow-auto">
                            <table className="react-table min-w-[1180px]">
                                <thead>
                                    <tr>
                                        <th className="!text-left">Дата / время</th>
                                        <th className="!text-left">Меню</th>
                                        <th className="!text-left">Стол(ы)</th>
                                        <th className="!text-left">Статус</th>
                                        <th className="!text-left">Позиций</th>
                                        <th className="!text-left">Сумма</th>
                                        <th className="!text-left">Идентификатор клиента</th>
                                        <th className="!text-left">Статус оплаты</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersData?.orders.map((order, index) => {
                                        const orderKey = order.id || `order-${index}`
                                        const isExpanded = Boolean(order.id && expandedOrderId === order.id)

                                        return (
                                            <Fragment key={orderKey}>
                                                <tr
                                                    onClick={() => handleOrderClick(order)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter" || event.key === " ") handleOrderClick(order)
                                                    }}
                                                    tabIndex={order.id ? 0 : undefined}
                                                    className={order.id ? "cursor-pointer transition hover:bg-[#F7F8FA]" : ""}
                                                >
                                                    <td>{formatDateTime(order.createdAt)}</td>
                                                    <td className="!whitespace-normal">
                                                        {order.menuId ? menuNameById.get(order.menuId) || order.menuId : <EmptyValue />}
                                                    </td>
                                                    <td className="!whitespace-normal">{order.tableIds.length ? order.tableIds.join(", ") : <EmptyValue />}</td>
                                                    <td>{order.status || <EmptyValue />}</td>
                                                    <td>{formatNumber(order.itemsCount)}</td>
                                                    <td>{formatMoney(order.calculatedTotal)}</td>
                                                    {/* N4 list/card DTOs expose no client identifier — do not surface order.id here. */}
                                                    <td><EmptyValue /></td>
                                                    <td><EmptyValue /></td>
                                                </tr>
                                                {isExpanded && order.id && (
                                                    <tr>
                                                        <td colSpan={8} className="!whitespace-normal !bg-[#F7F8FA] !px-5">
                                                            <OrderDetails
                                                                state={detailsById[order.id]}
                                                                onRetry={() => fetchOrderDetails(order.id as string)}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={ordersData?.page || filters.page}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            setPage={(page) => dispatch(setRestaurantMenuAnalyticsPage(page))}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
